import { html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import { pb } from "../api/client.js";
import { humanError } from "../api/errors.js";
import { search, type ScopeRecord, type SearchItem } from "../api/registry.js";
import { myRoleInTeam } from "../api/teams.js";
import { roleAtLeast, type Role } from "../lib/roles.js";
import { previewType } from "../lib/preview.js";
import { formatCount } from "../lib/format.js";
import { downloadIcon } from "../lib/icons.js";
import "../components/ibcs-preview.js";

/**
 * page-scopes shows a scope (public read) with its packages — the design's
 * "Publisher Profile" (global-unique name, no `@` stored). Scope creation is
 * superuser-only (the scopes collection's write rules are nil); this page is
 * read-only, there is no self-service way to claim or take over a scope.
 */
@customElement("page-scopes")
export class PageScopes extends PageElement {
  static override styles = [
    shared,
    css`
      .frame {
        border: 1px solid var(--border);
        border-radius: 16px;
        overflow: hidden;
        background: var(--surface);
        margin-bottom: var(--sp-5);
      }
      .profile {
        display: flex;
        align-items: flex-start;
        gap: 18px;
        padding: 30px 28px 22px;
        border-bottom: 1px solid var(--border);
      }
      .avatar {
        width: 60px;
        height: 60px;
        border-radius: 15px;
        background: var(--brand);
        color: var(--brand-contrast);
        display: flex;
        align-items: center;
        justify-content: center;
        font: 800 30px/1 var(--display);
        flex-shrink: 0;
        text-transform: lowercase;
      }
      .identity {
        flex: 1;
        min-width: 0;
      }
      .identity h1 {
        font: 600 24px/1 var(--mono);
        color: var(--text);
        margin: 0 0 6px;
      }
      .bio {
        font: 400 14px/1.55 var(--sans);
        color: var(--text-dim);
        margin: 0 0 16px;
        max-width: 520px;
      }
      .stats {
        display: flex;
        align-items: center;
        gap: 26px;
        flex-wrap: wrap;
      }
      .stat {
        display: flex;
        align-items: baseline;
        gap: 7px;
      }
      .stat .num {
        font: 600 17px/1 var(--mono);
        color: var(--text);
      }
      .stat .lbl {
        font: 400 12.5px/1 var(--sans);
        color: var(--text-faint);
      }
      .divider {
        width: 1px;
        height: 14px;
        background: var(--border);
      }
      .actions {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .ext {
        display: inline-flex;
        align-items: center;
        height: 36px;
        padding: 0 16px;
        border: 1px solid var(--border-strong);
        border-radius: 9px;
        background: var(--surface);
        color: var(--text);
        font: 600 13px/1 var(--sans);
      }
      .ext:hover {
        text-decoration: none;
        border-color: var(--brand);
      }
      .subtabs {
        display: flex;
        gap: 22px;
        padding: 0 28px;
        border-bottom: 1px solid var(--border);
      }
      .subtab {
        font: 600 13.5px/1 var(--sans);
        color: var(--text);
        padding: 14px 0;
        border-bottom: 2px solid var(--brand);
      }
      .subtab .c {
        color: var(--text-faint);
        font-weight: 500;
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
        padding: 20px 28px 28px;
      }
      @media (max-width: 900px) {
        .grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      @media (max-width: 600px) {
        .grid {
          grid-template-columns: 1fr;
        }
        .profile {
          flex-wrap: wrap;
        }
      }
      .pcard {
        border: 1px solid var(--border);
        border-radius: 11px;
        background: var(--surface);
        overflow: hidden;
        display: block;
      }
      .pcard:hover {
        text-decoration: none;
        border-color: var(--brand);
      }
      .pcard .well {
        height: 104px;
        border-bottom: 1px solid var(--border);
        background: var(--surface-2);
        padding: 11px 13px;
      }
      .pcard .body {
        padding: 13px 15px 15px;
      }
      .pcard .name {
        font: 600 13.5px/1.2 var(--mono);
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 7px;
      }
      .pcard .desc {
        font: 400 12.5px/1.45 var(--sans);
        color: var(--text-dim);
        margin: 0 0 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .pcard .crow {
        display: flex;
        align-items: center;
        gap: 10px;
        font: 500 11.5px/1 var(--mono);
        color: var(--text-faint);
      }
      .pcard .crow .v {
        color: var(--text-dim);
      }
      .pcard .crow .dl {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .pcard .crow .k {
        margin-left: auto;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .empty {
        padding: 40px 28px;
        color: var(--text-dim);
      }
    `,
  ];

  @property() scope = "";

  @state() private record: ScopeRecord | null = null;
  @state() private packages: SearchItem[] = [];
  @state() private myRole: Role | null = null;
  @state() private errorMsg = "";

  override willUpdate(changed: PropertyValues) {
    if (changed.has("scope")) void this.load();
  }

  private async load() {
    this.setTitle(this.scope ? `@${this.scope}` : "Scopes");
    this.errorMsg = "";
    if (!this.scope) {
      this.record = null;
      this.packages = [];
      this.myRole = null;
      return;
    }
    try {
      this.record = await pb
        .collection("scopes")
        .getFirstListItem<ScopeRecord>(pb.filter("name = {:n}", { n: this.scope }), {
          expand: "team",
        })
        .catch(() => null as unknown as ScopeRecord);
      this.myRole = this.record?.team ? await myRoleInTeam(this.record.team).catch(() => null) : null;
      const res = await search({ scope: [this.scope], perPage: 50, facets: false });
      this.packages = res.items;
    } catch (e) {
      this.errorMsg = humanError(e);
    }
  }

  private renderProfile() {
    const team = this.record?.expand?.team;
    const installs = this.packages.reduce((sum, p) => sum + (p.pulls30d || 0), 0);
    const since = this.record?.created ? new Date(this.record.created).getFullYear() : null;
    const bio = team
      ? `Components published under @${this.scope}, maintained by ${team.display_name || team.name}.`
      : `Report components published under the @${this.scope} scope.`;
    return html`
      <div class="profile">
        <div class="avatar" aria-hidden="true">${(this.scope[0] || "@").toLowerCase()}</div>
        <div class="identity">
          <h1>@${this.scope}</h1>
          <p class="bio">${bio}</p>
          <div class="stats">
            <div class="stat">
              <span class="num">${formatCount(this.packages.length)}</span>
              <span class="lbl">components</span>
            </div>
            <div class="divider"></div>
            <div class="stat">
              <span class="num">${formatCount(installs)}</span>
              <span class="lbl">installs / 30d</span>
            </div>
            ${since
              ? html`<div class="divider"></div>
                  <div class="stat">
                    <span class="num">${since}</span>
                    <span class="lbl">since</span>
                  </div>`
              : ""}
          </div>
        </div>
        <div class="actions">
          ${roleAtLeast(this.myRole, "maintainer")
            ? html`<a class="ext" href=${`/scopes/${this.scope}/deleted`}>Deleted packages</a>`
            : ""}
        </div>
      </div>
      <div class="subtabs">
        <span class="subtab">Components <span class="c">${this.packages.length}</span></span>
      </div>
    `;
  }

  private renderGrid(items: SearchItem[], emptyMessage: string) {
    if (!items.length) {
      return html`<div class="empty">${emptyMessage}</div>`;
    }
    return html`<div class="grid">
      ${items.map((p) => {
        const coord = p.scope.replace(/^@/, "");
        const desc = p.title || p.description || "";
        return html`<a
          class="pcard"
          href=${`/p/${coord}/${p.name}`}
          aria-label=${desc ? `${p.name} — ${desc}` : p.name}
        >
          <div class="well"><ibcs-preview type=${previewType(p.kind, p.category)}></ibcs-preview></div>
          <div class="body">
            <div class="name">${p.name}</div>
            <p class="desc">${desc}</p>
            <div class="crow">
              <span class="v">v${p.latestVersion || "–"}</span>
              <span class="dl">${downloadIcon(11)} ${formatCount(p.pulls30d)}</span>
              <span class="k">${p.kind}</span>
            </div>
          </div>
        </a>`;
      })}
    </div>`;
  }

  override render() {
    const publicPackages = this.packages.filter((p) => p.visibility !== "private");
    const privatePackages = this.packages.filter((p) => p.visibility === "private");
    return html`
      <div class="wrap">
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.scope
          ? html`<div class="frame">
              ${this.renderProfile()} ${this.renderGrid(publicPackages, "No packages in this scope yet.")}
              ${privatePackages.length
                ? html`
                    <div class="subtabs">
                      <span class="subtab">Private <span class="c">${privatePackages.length}</span></span>
                    </div>
                    ${this.renderGrid(privatePackages, "")}
                  `
                : ""}
            </div>`
          : html`<h1>Scopes</h1>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-scopes": PageScopes;
  }
}
